/**
 * Tenancy Slide-over Alpine.js Component
 *
 * Manages the tenancy detail slide-over panel state and data fetching.
 * Follows the existing pattern from property_show_components.js.
 */

import consumer from "../channels/consumer"
import { notesMixin, destroyNote } from './notes_mixin'
import { gravatarUrl } from '../utils/md5'
import * as compliance from '../utils/compliance_status'
import * as contactGroup from '../utils/contact_group'
import { accountTabMixin } from './account_tab_mixin'
import { legacyImportMixin } from './legacy_import_mixin'
import { transactionSearchMixin } from './transaction_search_mixin'
import { tenantStatementModalMixin } from './tenant_statement_modal_mixin'

// Dates the way the rest of the app writes them (see job_slideover.js), with
// an optional weekday for the "Send on Monday" case where the day of the week
// is the point.
function ukDate(value, { weekday = false, short = false } = {}) {
  if (!value) return ''
  const date = value instanceof Date ? value : new Date(value)
  if (isNaN(date)) return ''
  const options = { day: 'numeric', month: short ? 'short' : 'long' }
  if (!short) options.year = 'numeric'
  const formatted = date.toLocaleDateString('en-GB', options)
  if (!weekday) return formatted
  const day = date.toLocaleDateString('en-GB', { weekday: 'long' })
  return short ? day : `${day} ${formatted}`
}

export function tenancySlideover() {
  return {
    ...notesMixin,
    ...accountTabMixin('tenancy'),
    ...legacyImportMixin('tenancy'),
    ...transactionSearchMixin('tenancy'),
    ...tenantStatementModalMixin(),

    // State
    open: false,
    loading: false,
    error: null,
    tenancy: null,
    tenancyId: null,
    subscription: null,
    zIndex: 100010,

    // Mode: 'view' for existing tenancy, 'create' for new tenancy
    mode: 'view',

    // Create mode form state
    form: {
      property_id: null,
      property_search: '',
      property_results: [],
      searching_properties: false,
      show_property_dropdown: false,
      selected_property: null,
      start_date: '',
      current_rent_amount: '',
      deposit_amount: '',
      deposit_scheme: '',
      rent_due_day: '',
      service_type: '',
      is_hmo: false,
      special_conditions: '',
      tenants: [],
      tenant_search: '',
      tenant_results: [],
      searching_tenants: false,
      show_tenant_dropdown: false,
      show_new_tenant_form: false,
      new_tenant: {
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
      }
    },

    // Form submission state
    saving: false,
    formErrors: {},

    // Permitted-occupier form (view mode)
    occupierFormOpen: false,
    occupierSaving: false,
    occupierError: null,
    occupierForm: {
      first_name: '',
      last_name: '',
      email: '',
      phone: ''
    },

    // Available options for dropdowns
    availableServiceTypes: [],
    availableDepositSchemes: [],

    // Accordion state (object allows multiple open)
    openSections: {},

    // AI panel state (for backdrop suppression)
    aiPanelOpen: false,

    // Tab state for view mode
    activeTab: 'overview',

    // Deposit modal state
    depositModalOpen: false,
    depositSaving: false,
    depositError: null,
    depositForm: {
      scheme: '',
      registration_number: '',
      registered_at: '',
      newCertificates: [],
      certificatesToDelete: [],
      dragging: false
    },
    depositSchemes: [],
    existingCertificates: [],

    // Documents form state (for tenancy documents/ASTs)
    documentsForm: {
      dragging: false,
      documentsToDelete: [],
      saving: false
    },

    // An inventory dropped on the Documents tab is a decision, not just a
    // file: it can be sent to the tenants to comment on and sign. Nothing is
    // filed until a button in the modal is pressed.
    inventoryModal: {
      open: false,
      analysing: false,
      unreadable: false,
      filing: false,
      file: null,
      extracted: null,
      context: null,
      commentDays: 10,

      get hasHighWarning() {
        return (this.context?.warnings || []).some(w => w.severity === 'high')
      },

      get title() {
        if (this.unreadable) return this.file?.name || 'Document'
        return this.extracted?.kind === 'check_out'
          ? 'Schedule of Condition — Check Out'
          : 'Inventory & Schedule of Condition'
      },

      get subtitle() {
        if (this.unreadable) return this.file ? `${(this.file.size / 1024 / 1024).toFixed(1)} MB` : ''
        const bits = []
        if (this.extracted?.provider) bits.push(this.extracted.provider)
        if (this.extracted?.clerk_reference) bits.push(`clerk ${this.extracted.clerk_reference}`)
        if (this.extracted?.compiled_on) bits.push(`compiled ${ukDate(this.extracted.compiled_on)}`)
        if (this.extracted?.page_count) bits.push(`${this.extracted.page_count} pages`)
        return bits.join(' · ')
      },

      // The invitation waits for the tenancy to start — there is no point
      // serving an inventory on someone who has not taken possession.
      get timingMessage() {
        const who = (this.context?.recipients || []).length > 1 ? 'tenants' : 'tenant'
        const when = this.context?.starts_in_future
          ? `on <strong class="font-semibold">${ukDate(this.context?.send_on, { weekday: true })}</strong>`
          : 'today'
        return `The inventory will be emailed to the ${who} ${when}.`
      },

      get daysMessage() {
        const days = parseInt(this.commentDays, 10)
        if (!days || days < 1) return 'Enter a number of days.'
        if (days < 7) return `The report itself gives the tenant 7 days — ${days} is shorter than what they have already been told.`
        const deadline = new Date(this.context?.send_on)
        deadline.setDate(deadline.getDate() + days)
        const names = (this.context?.recipients || []).map(r => r.name.split(' ')[0])
        const who = names.length === 1 ? names[0] : 'They'
        return `${who} ${names.length === 1 ? 'has' : 'have'} until ${ukDate(deadline)}.`
      },

      get sendLabel() {
        if (this.filing) return 'Filing…'
        if (this.unreadable) return "It's an inventory"
        if (!this.context?.sendable) return 'Send to tenants'
        if (this.hasHighWarning) return 'Send anyway'
        if (!this.context?.starts_in_future) return 'Send now'
        const when = ukDate(this.context?.send_on, { weekday: true, short: true })
        return `Send on ${when}`
      }
    },

    // Note form state (for adding timeline notes)
    noteForm: {
      content: '',
      saving: false
    },

    // Initialize component
    init() {
      // CRITICAL: Capture Alpine context for ActionCable callbacks
      const self = this

      // Subscribe to ActionCable for real-time updates
      this.subscription = consumer.subscriptions.create(
        { channel: "DataUpdatesChannel" },
        {
          received(message) {
            // Filter for tenancy updates matching our current tenancy
            if (message.type === 'tenancy' && self.tenancy && message.id === self.tenancy.id) {
              console.debug('[TenancySlideover] Received update for tenancy', message.id)
              self.handleTenancyUpdate(message)
            }

            // Audit logs broadcast on creation. Pick up any whose auditable
            // is the currently-open tenancy and silently refresh so the
            // History tab shows them without a manual reload.
            if (
              message.type === 'audit_log' &&
              self.tenancy &&
              message.data &&
              message.data.auditable_type === 'Tenancy' &&
              message.data.auditable_id === self.tenancy.id
            ) {
              console.debug('[TenancySlideover] Audit log added for tenancy', self.tenancy.id)
              self.fetchTenancy(true)
            }
          }
        }
      )

      // Track AI panel state for backdrop suppression
      window.addEventListener('ai-panel-toggled', (e) => {
        this.aiPanelOpen = e.detail.open
      })

      // Listen for open requests
      this.$watch('open', (value) => {
        if (value && this.tenancyId && !this.tenancy) {
          this.fetchTenancy()
        }
      })

      // Pre-load deposit schemes so they're ready when modal opens
      this.fetchDepositSchemes()

      // Listen for tenancy-updated events (from end tenancy modal)
      window.addEventListener('tenancy-updated', (event) => {
        if (self.tenancy && event.detail.tenancy && event.detail.tenancy.id === self.tenancy.id) {
          Object.assign(self.tenancy, event.detail.tenancy)
        }
      })

      // Listen for create mode requests (from AI Assistant or other triggers)
      window.addEventListener('open-tenancy-slideover-create', (event) => {
        self.openForCreate(event.detail || {})
      })

      // Lazy-load account data when Account tab is first selected
      this.$watch('activeTab', (tab) => {
        if (tab === 'account' && !this.acctDataLoaded && this.tenancyId) {
          this.fetchAccountData(true)
        }
      })

      // An inspection booked from the Inspections tab (shared modal) - refresh this tenancy
      window.addEventListener('inspection-booked', (event) => {
        if (this.tenancy && event.detail && event.detail.tenancyId === this.tenancy.id) {
          this.fetchTenancy(true)
        }
      })

      // Initialise account tab event listeners
      this.initAccountTab()
    },

    // Open slide-over for a specific tenancy (view mode)
    openForTenancy(tenancyId, tab) {
      const store = Alpine.store?.('slideovers')

      // If this tenancy is already open, just bring it to front
      if (store?.isOpen?.('tenancy', tenancyId)) {
        store.bringToFront('tenancy', tenancyId)
        // Switch tab if requested
        if (tab) this.activeTab = tab
        return
      }

      // Get next z-index from store for proper stacking
      if (store?.getNextZIndex) {
        this.zIndex = store.getNextZIndex()
      }
      this.mode = 'view'
      this.tenancyId = tenancyId
      this.tenancy = null
      this.error = null
      this.openSections = { documents: true, history: true }
      this.activeTab = tab || 'overview'
      this.noteForm = { content: '', saving: false }
      this.resetAccountState()
      this.open = true

      // Register this slideover
      if (store?.register) {
        store.register('tenancy', tenancyId, this)
      }

      this.fetchTenancy()

      // Explicitly fetch account data if opening on account tab
      // ($watch won't fire if activeTab was already 'account')
      if (this.activeTab === 'account') {
        this.fetchAccountData(true)
      }
    },

    // Open slide-over for creating a new tenancy
    openForCreate(prepopulatedData = {}) {
      // Get next z-index from store for proper stacking
      const store = Alpine.store?.('slideovers')
      if (store?.getNextZIndex) {
        this.zIndex = store.getNextZIndex()
      }
      this.mode = 'create'
      this.tenancyId = null
      this.tenancy = null
      this.error = null
      this.formErrors = {}
      this.saving = false

      // Reset form with prepopulated data
      this.form = {
        property_id: prepopulatedData.property_id || null,
        property_search: prepopulatedData.property_name || '',
        property_results: [],
        searching_properties: false,
        show_property_dropdown: false,
        selected_property: prepopulatedData.property_id ? {
          id: prepopulatedData.property_id,
          display_name: prepopulatedData.property_name || '',
          full_address: prepopulatedData.full_address || ''
        } : null,
        start_date: prepopulatedData.start_date || '',
        current_rent_amount: '',
        deposit_amount: '',
        deposit_scheme: '',
        rent_due_day: '',
        service_type: '',
        is_hmo: false,
        special_conditions: '',
        tenants: prepopulatedData.tenants || [],
        tenant_search: '',
        tenant_results: [],
        searching_tenants: false,
        show_tenant_dropdown: false,
        show_new_tenant_form: false,
        new_tenant: {
          first_name: '',
          last_name: '',
          email: '',
          phone: ''
        }
      }

      // Set default accordions for create mode
      this.openSections = { specialConditions: false }

      // Fetch form options
      this.fetchFormOptions()

      this.open = true
    },

    // Fetch service types and other form options
    async fetchFormOptions() {
      try {
        const response = await fetch('/tenancies/new.json', {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (response.ok) {
          const data = await response.json()
          this.availableServiceTypes = data.service_types || []
          // Use existing deposit schemes from the deposit modal
          if (!this.depositSchemes || this.depositSchemes.length === 0) {
            await this.fetchDepositSchemes()
          }
          // Transform deposit schemes to use 'value' instead of 'key' for consistency with the form
          this.availableDepositSchemes = this.depositSchemes.map(s => ({
            value: s.key,
            label: s.label
          }))
        }
      } catch (error) {
        console.error('Failed to fetch form options:', error)
        // Provide fallback deposit schemes
        this.availableDepositSchemes = [
          { value: 'dps', label: 'DPS' },
          { value: 'tds', label: 'TDS' },
          { value: 'mydeposits', label: 'MyDeposits' }
        ]
      }
    },

    // Close slide-over
    close() {
      // Unregister from store
      const store = Alpine.store('slideovers')
      if (store && this.tenancyId) {
        store.unregister('tenancy', this.tenancyId)
      }

      this.resetAccountState()
      this.clearSearch()
      this.open = false

      // Close AI panel if it was open in our context
      window.dispatchEvent(new CustomEvent('account-slideover-closed'))
    },

    // ========== Permitted occupiers ==========
    // Live there with the landlord's permission, not party to the tenancy.

    async addOccupier() {
      if (this.occupierSaving) return
      this.occupierSaving = true
      this.occupierError = null

      try {
        const response = await fetch(`/tenancies/${this.tenancy.id}/occupiers`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({ occupier: this.occupierForm })
        })
        const data = await response.json().catch(() => ({}))
        if (!response.ok) throw new Error((data.errors || ['Could not add the occupier']).join(', '))

        this.occupierFormOpen = false
        this.occupierForm = { first_name: '', last_name: '', email: '', phone: '' }
        await this.fetchTenancy(true)
      } catch (error) {
        this.occupierError = error.message
      } finally {
        this.occupierSaving = false
      }
    },

    async removeOccupier(occupier) {
      if (!window.confirm(`Remove ${occupier.full_name} as a permitted occupier?`)) return

      try {
        const response = await fetch(`/tenancies/${this.tenancy.id}/occupiers/${occupier.id}`, {
          method: 'DELETE',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })
        if (!response.ok) {
          const data = await response.json().catch(() => ({}))
          throw new Error((data.errors || ['Could not remove the occupier']).join(', '))
        }
        await this.fetchTenancy(true)
      } catch (error) {
        window.dispatchEvent(new CustomEvent('toast', { detail: { message: error.message, type: 'error' } }))
      }
    },

    // Fetch tenancy data from server
    // silent: if true, don't show loading skeleton (for background refreshes)
    async fetchTenancy(silent = false) {
      if (!this.tenancyId) return

      if (!silent) {
        this.loading = true
      }
      this.error = null

      try {
        const response = await fetch(`/tenancies/${this.tenancyId}.json`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        this.tenancy = await response.json()
      } catch (error) {
        console.error('Failed to fetch tenancy:', error)
        this.error = 'Failed to load tenancy details. Please try again.'
      } finally {
        this.loading = false
      }
    },

    // Handle real-time updates from ActionCable
    handleTenancyUpdate(message) {
      if (!this.tenancy || message.id !== this.tenancy.id) return

      switch (message.action) {
        case 'update':
          // The broadcast payload only carries the slim
          // `tenancy_data_for_broadcast` shape — it doesn't include
          // documents, timeline or other slideover-only data. Pull the
          // full payload silently so attachments and history stay in
          // sync after background jobs run.
          this.fetchTenancy(true)
          break
        case 'destroy':
          this.close()
          break
      }
    },

    // Computed helpers for template
    get statusBadgeColor() {
      if (!this.tenancy) return 'gray'
      switch (this.tenancy.status) {
        case 'active': return 'green'
        case 'upcoming': return 'blue'
        default: return 'gray'
      }
    },

    get hasAnyDetails() {
      return this.tenancy?.special_conditions || this.tenancy?.imported_notes
    },

    // Responsive font size for the stats strip — keeps all 4 values the same size
    // The figure is the vocabulary's 19px; only a value too long for its box
    // steps it down, so the strip matches the property's at rest
    get statValueClass() {
      if (!this.tenancy) return ''
      const lengths = [
        this.formatCurrency(this.tenancy.current_rent_amount).length,
        (this.tenancy.next_rent_due_date_display || '—').length,
        this.formatCurrency(this.tenancy.account_balance).length,
        this.formatCurrency(this.tenancy.deposit_amount).length
      ]
      const max = Math.max(...lengths)
      return max > 12 ? '!text-[17px]' : ''
    },

    // "7th" — the day of the month rent falls due, for the Rent stat's qualifier
    ordinalDay(n) {
      if (!n) return ''
      const rem = n % 100
      const suffix = (rem >= 11 && rem <= 13) ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th')
      return `${n}${suffix}`
    },

    // "in 11 days" / "due today" / "3 days ago" — the Next due stat's qualifier
    dueInWords() {
      const iso = this.tenancy?.next_rent_due_date
      if (!iso) return ''
      const today = new Date(); today.setHours(0, 0, 0, 0)
      const due = new Date(iso + 'T00:00:00')
      const days = Math.round((due - today) / 86400000)
      if (days === 0) return 'due today'
      if (days === 1) return 'tomorrow'
      if (days === -1) return 'yesterday'
      if (days > 0) return `in ${days} days`
      return `${-days} days ago`
    },

    // The Deposit protected row: one word in the pill, one line beneath the
    // title. Registered is not protected: until the scheme's certificate is on
    // the deposit the pill stays amber.
    depositState() {
      const t = this.tenancy || {}
      const green = 'bg-[#E3F1E7] text-[#1F6B3E]'
      const amber = 'bg-[#FBEBD2] text-[#8A5A0E]'
      const grey = 'bg-[#ECEBE6] text-[#4B4B48]'
      if (t.deposit_transferred_to_landlord) return { pill: grey, label: 'Landlord held', line: 'Held by the landlord' }
      if (t.deposit_awaiting_certificate) return { pill: amber, label: 'Awaiting certificate', line: t.deposit_registered_at_display ? `Registered ${t.deposit_registered_at_display}` : 'Registered, no certificate yet' }
      if (t.deposit_registration_number) return { pill: green, label: 'Done', line: t.deposit_protected_on_display || t.deposit_registered_at_display || 'Protected' }
      if (t.deposit_amount > 0) return { pill: amber, label: 'Not protected', line: 'No scheme registration' }
      return { pill: grey, label: 'None', line: 'No deposit taken' }
    },

    // Format currency. Show pennies only when the amount actually has them, so
    // round figures (rent, deposit) stay clean while balances like £739.73 show
    // exactly rather than rounding to £740.
    formatCurrency(amount) {
      if (amount == null) return '—'
      const hasPence = Math.round(amount * 100) % 100 !== 0
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: hasPence ? 2 : 0,
        maximumFractionDigits: hasPence ? 2 : 0
      }).format(amount)
    },

    // Format percentage
    formatPercentage(value) {
      if (value == null) return ''
      return `+${value.toFixed(1)}%`
    },

    // Compliance tiles share their colours, icons and wording with the
    // property slideover (utils/compliance_status.js).
    complianceStatusColor(status) { return compliance.statusColor(status) },
    complianceIconColor(status) { return compliance.iconColor(status) },
    complianceIcon(status) { return compliance.icon(status) },
    complianceExpiryText(item) { return compliance.expiryText(item) },
    compliancePillClass(status) { return compliance.pillClass(status) },
    compliancePillLabel(status, words) { return compliance.pillLabel(status, words) },

    // The EPC tile opens the same modal the property slideover uses; the
    // payload arrives pre-mapped to the modal's shape.
    openEpcModal() {
      if (!window.openEpcModal || !this.tenancy?.property) return
      window.openEpcModal(this.tenancy.property.id, this.tenancy.property.full_address, this.tenancy.compliance?.epc_modal || null)
    },

    // Open deposit protection modal
    openDepositModal() {
      // Populate form with current values
      // Use ?? instead of || for amount to preserve 0 values
      this.depositForm = {
        amount: this.tenancy?.deposit_amount ?? '',
        transferred_to_landlord: this.tenancy?.deposit_transferred_to_landlord || false,
        scheme: this.tenancy?.deposit_scheme || '',
        registration_number: this.tenancy?.deposit_registration_number || '',
        registered_at: this.tenancy?.deposit_registered_at || '',
        newCertificates: [],
        certificatesToDelete: [],
        dragging: false
      }
      // Load existing certificates from tenancy data
      this.existingCertificates = this.tenancy?.deposit_protection_certificates || []
      this.depositError = null
      this.depositModalOpen = true

      // Fetch deposit schemes from server if not already loaded
      if (this.depositSchemes.length === 0) {
        this.fetchDepositSchemes()
      }
    },

    // Close deposit protection modal
    closeDepositModal() {
      this.depositModalOpen = false
      this.depositError = null
    },

    // Fetch available deposit schemes for this agency
    async fetchDepositSchemes() {
      try {
        const response = await fetch('/api/deposit_schemes', {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) {
          throw new Error('Failed to fetch deposit schemes')
        }

        this.depositSchemes = await response.json()
      } catch (error) {
        console.error('Failed to fetch deposit schemes:', error)
        // Fall back to default schemes
        this.depositSchemes = [
          { key: 'dps', label: 'DPS' },
          { key: 'tds', label: 'TDS' },
          { key: 'mydeposits', label: 'MyDeposits' }
        ]
      }
    },

    // Save deposit protection details
    async saveDepositProtection() {
      this.depositSaving = true
      this.depositError = null

      try {
        const formData = new FormData()
        formData.append('tenancy[deposit_amount]', this.depositForm.amount || '')

        // Set deposit_status based on transferred_to_landlord toggle
        if (this.depositForm.transferred_to_landlord) {
          formData.append('tenancy[deposit_status]', 'transferred')
          // Clear agency registration fields when transferred
          formData.append('tenancy[deposit_scheme]', '')
          formData.append('tenancy[deposit_registration_number]', '')
        } else {
          // Reset to registered status if there's a registration number
          if (this.depositForm.registration_number) {
            formData.append('tenancy[deposit_status]', 'registered')
          }
          formData.append('tenancy[deposit_scheme]', this.depositForm.scheme)
          formData.append('tenancy[deposit_registration_number]', this.depositForm.registration_number)
          formData.append('tenancy[deposit_registered_at]', this.depositForm.registered_at)
        }

        // Add new certificates (multiple files)
        if (!this.depositForm.transferred_to_landlord) {
          for (const file of this.depositForm.newCertificates) {
            formData.append('tenancy[deposit_protection_certificates][]', file)
          }
        }

        // Add certificates to delete
        for (const certId of this.depositForm.certificatesToDelete) {
          formData.append('certificates_to_delete[]', certId)
        }

        const response = await fetch(`/api/tenancies/${this.tenancy.id}/deposit_protection`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: formData
        })

        if (!response.ok) {
          const data = await response.json()
          throw new Error(data.error || 'Failed to save deposit protection')
        }

        const updatedTenancy = await response.json()

        // Update local tenancy data
        Object.assign(this.tenancy, updatedTenancy)

        // Close modal
        this.closeDepositModal()
      } catch (error) {
        console.error('Failed to save deposit protection:', error)
        this.depositError = error.message || 'Failed to save. Please try again.'
      } finally {
        this.depositSaving = false
      }
    },

    // Add deposit certificates (from file input or drag-and-drop)
    addDepositCertificates(files) {
      const fileArray = Array.from(files)
      this.depositForm.newCertificates = [...this.depositForm.newCertificates, ...fileArray]
    },

    // Remove a new certificate before upload
    removeNewCertificate(index) {
      this.depositForm.newCertificates.splice(index, 1)
    },

    // Toggle certificate for deletion
    toggleCertificateDeletion(certId) {
      const index = this.depositForm.certificatesToDelete.indexOf(certId)
      if (index > -1) {
        // Restore - remove from deletion list
        this.depositForm.certificatesToDelete.splice(index, 1)
      } else {
        // Mark for deletion
        this.depositForm.certificatesToDelete.push(certId)
      }
    },

    // Get Gravatar URL for a tenant's email
    getTenantGravatarUrl(email, size = 40) {
      return gravatarUrl(email, size)
    },

    // Get initials from a full name (e.g. "Dylan Andrew" → "DA"). Bracketed nicknames like "Philip (Pip) Clarke" → "PC".
    getInitials(name) {
      if (!name) return '?'
      name = name.replace(/\([^)]*\)|\[[^\]]*\]/g, ' ')
      return name.split(' ').map(n => n[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
    },

    // Contact group headings and the bulk "Email both" pill follow one rule
    // shared with the property and job slideovers — utils/contact_group.js.
    contactGroupLabel(contacts, one, many) { return contactGroup.groupLabel(contacts, one, many) },
    bulkEmailLabel(contacts) { return contactGroup.bulkEmailLabel(contacts) },
    emailGroup(contacts, type) { contactGroup.emailGroup(contacts, type, this.tenancy?.property?.display_name) },

    // Open end tenancy modal — now a two-path picker (agreed end date /
    // Section 8 notice). The choice modal dispatches the follow-up event.
    openEndTenancyModal() {
      if (!this.tenancy) return
      window.dispatchEvent(new CustomEvent('open-end-tenancy-choice-modal', {
        detail: { tenancy: this.tenancy }
      }))
    },

    // Open rent increase modal
    openRentIncreaseModal() {
      if (!this.tenancy) return
      window.dispatchEvent(new CustomEvent('open-rent-increase-modal', {
        detail: { tenancy: this.tenancy }
      }))
    },

    // Document management methods
    //
    // Every PDF is read BEFORE anything is filed. Ordinary documents — an EPC,
    // a signed agreement — file themselves as they always did; an inventory
    // raises the modal, because it carries a decision the agent has to make
    // while they are still thinking about the file they just dropped.
    async addDocuments(files) {
      const fileArray = Array.from(files).filter(f => f.type === 'application/pdf')
      if (fileArray.length === 0) return

      this.inventoryModal.file = fileArray[0]
      this.inventoryModal.extracted = null
      this.inventoryModal.context = null
      this.inventoryModal.unreadable = false
      this.inventoryModal.analysing = true
      this.inventoryModal.open = true

      const readings = await Promise.all(fileArray.map(f => this.analyseDocument(f)))

      const ordinary = []
      let decision = null
      fileArray.forEach((file, i) => {
        const reading = readings[i]
        if (!decision && reading && (reading.manual_fallback || reading.extracted?.is_inventory)) {
          decision = { file, reading }
        } else {
          ordinary.push(file)
        }
      })

      if (ordinary.length) await this.uploadDocuments(ordinary)

      if (!decision) {
        this.closeInventoryModal()
        return
      }

      this.inventoryModal.file = decision.file
      this.inventoryModal.unreadable = !!decision.reading.manual_fallback
      this.inventoryModal.extracted = decision.reading.extracted || null
      this.inventoryModal.context = decision.reading.context || { sendable: true, warnings: [] }
      this.inventoryModal.commentDays = this.inventoryModal.context.default_comment_days || 10
      this.inventoryModal.analysing = false
    },

    // Reads one document without storing it. A failure here is a failure to
    // READ, never a failure to upload — the file is still in the browser and
    // still fileable by hand.
    async analyseDocument(file) {
      const formData = new FormData()
      formData.append('document', file)
      try {
        const response = await fetch(`/tenancies/${this.tenancy.id}/analyse_document`, {
          method: 'POST',
          headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content },
          body: formData
        })
        const data = await response.json().catch(() => ({}))
        if (!response.ok) return data.manual_fallback ? { manual_fallback: true } : null
        return data
      } catch (error) {
        console.error('Failed to analyse document:', error)
        return null
      }
    },

    // Solid fills, matching the landlord documents grid. Brand blue is the
    // resting state; the colour only departs from it to say something —
    // amber while the tenants still owe an answer, emerald once signed, red
    // where the window shut without one.
    inventoryBadgeClasses(status) {
      return {
        pending_send: 'bg-brand-600',
        awaiting:     'bg-amber-600',
        signed:       'bg-emerald-600',
        expired:      'bg-red-600',
        filed:        'bg-gray-500',
        check_out:    'bg-gray-500'
      }[status] || 'bg-gray-500'
    },

    closeInventoryModal() {
      if (this.inventoryModal.filing) return
      this.inventoryModal.open = false
      this.inventoryModal.analysing = false
      this.inventoryModal.file = null
    },

    // Files the document either way; `send` additionally raises the inventory
    // and books the invitation for the day the tenancy starts.
    async confirmInventory(send) {
      if (!this.inventoryModal.file) return
      this.inventoryModal.filing = true
      try {
        const formData = new FormData()
        formData.append('document', this.inventoryModal.file)
        formData.append('send_to_tenants', send ? 'true' : 'false')
        formData.append('comment_days', this.inventoryModal.commentDays)
        const extracted = this.inventoryModal.extracted || {}
        for (const key of ['kind', 'provider', 'clerk_reference', 'compiled_on', 'page_count']) {
          if (extracted[key]) formData.append(key, extracted[key])
        }

        const response = await fetch(`/tenancies/${this.tenancy.id}/confirm_inventory`, {
          method: 'POST',
          headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content },
          body: formData
        })

        if (!response.ok) {
          const data = await response.json().catch(() => ({}))
          alert(data.error || 'Failed to file the document')
          return
        }

        this.inventoryModal.filing = false
        this.inventoryModal.open = false
        this.inventoryModal.file = null
        await this.fetchTenancy(true)
        this.pollForThumbnails()
      } catch (error) {
        console.error('Failed to file document:', error)
        alert('Failed to file the document. Please try again.')
      } finally {
        this.inventoryModal.filing = false
      }
    },

    // Upload documents immediately (no save button needed)
    async uploadDocuments(files) {
      this.documentsForm.saving = true
      try {
        const formData = new FormData()
        for (const file of files) {
          formData.append('documents[]', file)
        }

        const response = await fetch(`/tenancies/${this.tenancy.id}/update_documents`, {
          method: 'PATCH',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: formData
        })

        if (response.ok) {
          await this.fetchTenancy(true)  // Silent refresh - no loading flicker
          this.pollForThumbnails()
        } else {
          const data = await response.json().catch(() => ({}))
          alert(data.error || 'Failed to upload documents')
        }
      } catch (error) {
        console.error('Failed to upload documents:', error)
        alert('Failed to upload documents. Please try again.')
      } finally {
        this.documentsForm.saving = false
      }
    },

    toggleDocumentDeletion(docId) {
      const index = this.documentsForm.documentsToDelete.indexOf(docId)
      if (index > -1) {
        this.documentsForm.documentsToDelete.splice(index, 1)
      } else {
        this.documentsForm.documentsToDelete.push(docId)
      }
    },

    // Delete documents (called when confirming deletions)
    async saveDocuments() {
      if (this.documentsForm.documentsToDelete.length === 0) return

      this.documentsForm.saving = true
      try {
        const formData = new FormData()

        // Add IDs to delete
        for (const id of this.documentsForm.documentsToDelete) {
          formData.append('documents_to_delete[]', id)
        }

        const response = await fetch(`/tenancies/${this.tenancy.id}/update_documents`, {
          method: 'PATCH',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: formData
        })

        if (response.ok) {
          await this.fetchTenancy(true)  // Silent refresh - no loading flicker
          this.documentsForm.documentsToDelete = []
        } else {
          const data = await response.json().catch(() => ({}))
          alert(data.error || 'Failed to delete documents')
        }
      } catch (error) {
        console.error('Failed to delete documents:', error)
        alert('Failed to delete documents. Please try again.')
      } finally {
        this.documentsForm.saving = false
      }
    },

    // Poll for thumbnail updates on documents without thumbnails
    pollForThumbnails(attempts = 0) {
      const maxAttempts = 10  // Poll up to 10 times (30 seconds total)
      const pollInterval = 3000  // Every 3 seconds

      // Check if any documents are missing thumbnails
      const hasProcessingDocs = this.tenancy?.documents?.some(doc => !doc.thumbnail_url)

      if (!hasProcessingDocs || attempts >= maxAttempts || !this.open) {
        return  // Stop polling
      }

      setTimeout(async () => {
        if (!this.open) return  // Stop if slideover was closed

        await this.fetchTenancy(true)  // Silent refresh

        // Continue polling if still processing
        const stillProcessing = this.tenancy?.documents?.some(doc => !doc.thumbnail_url)
        if (stillProcessing && attempts < maxAttempts - 1) {
          this.pollForThumbnails(attempts + 1)
        }
      }, pollInterval)
    },

    // Add a note to the tenancy timeline
    async toggleRentChasing() {
      const enabled = !this.tenancy.rent_chasing_enabled
      const message = enabled
        ? 'Resume automated rent chasing for this tenancy? Chasing will pick up from the current arrears position.'
        : 'Pause automated rent chasing for this tenancy? Any active chase will be cancelled. You can resume chasing later.'
      if (!confirm(message)) return

      try {
        const response = await fetch(`/tenancies/${this.tenancy.id}/toggle_rent_chasing`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({ enabled })
        })

        if (response.ok) {
          const data = await response.json()
          this.tenancy.rent_chasing_enabled = data.rent_chasing_enabled
          this.tenancy.rent_chase_status = data.rent_chase_status
        } else {
          alert('Failed to update rent chasing setting')
        }
      } catch (error) {
        console.error('Failed to toggle rent chasing:', error)
        alert('Failed to update. Please try again.')
      }
    },

    // Open the Amend modal for a served Section 13 notice. The modal
    // handles all three post-serve interventions (reduce / challenge /
    // cancel) and submits to the appropriate controller action.
    openAmendRentIncrease(rentIncrease) {
      window.dispatchEvent(new CustomEvent('open-rent-increase-amend-modal', {
        detail: { tenancy: this.tenancy, rentIncrease: rentIncrease }
      }))
    },

    async addNote() {
      if (!this.noteForm.content?.trim()) return

      this.noteForm.saving = true
      try {
        const newNote = await this.saveNote({ type: 'Tenancy', id: this.tenancy.id }, this.noteForm.content)
        if (!this.tenancy.timeline) {
          this.tenancy.timeline = []
        }
        this.tenancy.timeline.unshift(newNote)
        this.tenancy.timeline_count = (this.tenancy.timeline_count || 0) + 1
        this.noteForm.content = ''
      } catch (error) {
        console.error('Failed to add note:', error)
        alert(error.message || 'Failed to add note. Please try again.')
      } finally {
        this.noteForm.saving = false
      }
    },
    removeTimelineEntry(entryId) {
      if (this.tenancy?.timeline) {
        this.tenancy.timeline = this.tenancy.timeline.filter(e => e.id !== entryId)
        this.tenancy.timeline_count = Math.max(0, (this.tenancy.timeline_count || 1) - 1)
      }
    },

    async deleteNote(noteId) {
      if (!confirm('Delete this note?')) return
      try {
        await destroyNote(noteId)
        this.removeTimelineEntry(noteId)
      } catch (error) {
        console.error('Failed to delete note:', error)
        alert('Failed to delete note. Please try again.')
      }
    },

    // ==========================================
    // Create Mode Methods
    // ==========================================

    // Search for properties
    async searchProperties() {
      const query = this.form.property_search
      if (!query || query.length < 2) {
        this.form.property_results = []
        this.form.show_property_dropdown = false
        return
      }

      this.form.searching_properties = true
      try {
        const response = await fetch(`/api/properties/search?q=${encodeURIComponent(query)}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (response.ok) {
          this.form.property_results = await response.json()
          this.form.show_property_dropdown = true
        }
      } catch (error) {
        console.error('Failed to search properties:', error)
      } finally {
        this.form.searching_properties = false
      }
    },

    // Select a property from search results
    selectProperty(property) {
      this.form.property_id = property.id
      this.form.selected_property = property
      this.form.property_search = property.display_name
      this.form.property_results = []
      this.clearFieldError('property_id')
    },

    // Clear selected property
    clearProperty() {
      this.form.property_id = null
      this.form.selected_property = null
      this.form.property_search = ''
      this.form.property_results = []
    },

    // Auto-set rent due day from start date
    updateRentDueDay() {
      if (this.form.start_date && !this.form.rent_due_day) {
        // Parse the date string directly to avoid timezone issues
        // Date format is YYYY-MM-DD, so split and get the day part
        const parts = this.form.start_date.split('-')
        if (parts.length === 3) {
          this.form.rent_due_day = parseInt(parts[2], 10)
        }
      }
    },

    // Calculate standard deposit based on monthly rent (UK legal limits)
    // 5 weeks for annual rent < £50,000, 6 weeks for >= £50,000
    // Always rounds DOWN to stay within legal limits
    calculateStandardDeposit(monthlyRent) {
      if (!monthlyRent || monthlyRent <= 0) return 0

      const annualRent = monthlyRent * 12
      const weeklyRent = annualRent / 52
      const weeks = annualRent >= 50000 ? 6 : 5

      return Math.floor(weeklyRent * weeks)
    },

    // Auto-calculate deposit when rent changes (only if deposit not already set)
    updateDepositFromRent() {
      const rent = parseFloat(this.form.current_rent_amount)
      if (rent > 0 && !this.form.deposit_amount) {
        this.form.deposit_amount = this.calculateStandardDeposit(rent)
      }
    },

    // Search for tenants (people)
    async searchTenants() {
      const query = this.form.tenant_search
      if (!query || query.length < 2) {
        this.form.tenant_results = []
        this.form.show_tenant_dropdown = false
        return
      }

      this.form.searching_tenants = true
      try {
        // Use the people search API with 'person' type filter
        const response = await fetch(`/api/people/search?q=${encodeURIComponent(query)}&types[]=person`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (response.ok) {
          const results = await response.json()
          // Filter out already selected tenants
          const selectedIds = this.form.tenants.filter(t => !t.is_new).map(t => t.id)
          this.form.tenant_results = results.filter(r => !selectedIds.includes(r.id))
          this.form.show_tenant_dropdown = true
        }
      } catch (error) {
        console.error('Failed to search tenants:', error)
      } finally {
        this.form.searching_tenants = false
      }
    },

    // Select a tenant from search results
    selectTenant(person) {
      this.form.tenants.push({
        id: person.id,
        full_name: person.name || person.full_name,
        email: person.email,
        phone: person.phone,
        is_organisation: person.is_organisation,
        is_new: false
      })
      this.form.tenant_search = ''
      this.form.tenant_results = []
      this.form.show_tenant_dropdown = false
      this.clearFieldError('tenants')
    },

    // Remove a tenant
    removeTenant(index) {
      this.form.tenants.splice(index, 1)
    },

    // Show new tenant inline form
    showNewTenantForm() {
      this.form.show_new_tenant_form = true
      this.form.new_tenant = {
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
      }
      // Pre-fill first name from search if it looks like a name
      if (this.form.tenant_search && !this.form.tenant_search.includes('@')) {
        const parts = this.form.tenant_search.trim().split(' ')
        this.form.new_tenant.first_name = parts[0] || ''
        this.form.new_tenant.last_name = parts.slice(1).join(' ') || ''
      }
      this.form.tenant_search = ''
      this.form.tenant_results = []
    },

    // Add the new tenant from inline form
    addNewTenant() {
      if (!this.form.new_tenant.first_name?.trim() || !this.form.new_tenant.last_name?.trim()) {
        return
      }

      const firstName = this.form.new_tenant.first_name.trim()
      const lastName = this.form.new_tenant.last_name.trim()

      this.form.tenants.push({
        is_new: true,
        first_name: firstName,
        last_name: lastName,
        full_name: `${firstName} ${lastName}`,
        email: this.form.new_tenant.email?.trim() || '',
        phone: this.form.new_tenant.phone?.trim() || '',
        is_organisation: false
      })

      this.form.show_new_tenant_form = false
      this.form.new_tenant = { first_name: '', last_name: '', email: '', phone: '' }
      this.clearFieldError('tenants')
    },

    // Cancel new tenant form
    cancelNewTenant() {
      this.form.show_new_tenant_form = false
      this.form.new_tenant = { first_name: '', last_name: '', email: '', phone: '' }
    },

    // Validate the create form
    validateForm() {
      this.formErrors = {}

      if (!this.form.property_id) {
        this.formErrors.property_id = ['Property is required']
      }

      if (!this.form.start_date) {
        this.formErrors.start_date = ['Start date is required']
      }

      // Validate new tenants have required fields
      this.form.tenants.forEach((tenant, index) => {
        if (tenant.is_new) {
          if (!tenant.first_name?.trim()) {
            this.formErrors[`tenant_${index}_first_name`] = ['First name is required']
          }
          if (!tenant.last_name?.trim()) {
            this.formErrors[`tenant_${index}_last_name`] = ['Last name is required']
          }
        }
      })

      return Object.keys(this.formErrors).length === 0
    },

    // Clear a specific field error
    clearFieldError(field) {
      delete this.formErrors[field]
    },

    // Humanize field names for error display
    humanizeFieldName(field) {
      const fieldNames = {
        property_id: 'Property',
        start_date: 'Start date',
        current_rent_amount: 'Monthly rent',
        deposit_amount: 'Deposit amount',
        deposit_scheme: 'Deposit scheme',
        rent_due_day: 'Rent due day',
        service_type: 'Service type',
        is_hmo: 'HMO status',
        special_conditions: 'Special conditions',
        tenants: 'Tenants',
        agency_id: 'Agency',
        status: 'Status',
        base: 'Error'
      }
      return fieldNames[field] || field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    },

    // Build the payload for creating a tenancy
    buildTenancyPayload() {
      return {
        tenancy: {
          property_id: this.form.property_id,
          start_date: this.form.start_date,
          initial_rent_amount: this.form.current_rent_amount || null,
          deposit_amount: this.form.deposit_amount || null,
          deposit_scheme: this.form.deposit_scheme || null,
          rent_due_day: this.form.rent_due_day || null,
          service_type: this.form.service_type || null,
          is_hmo: this.form.is_hmo,
          special_conditions: this.form.special_conditions || null
        },
        // Existing tenant person IDs
        person_ids: this.form.tenants
          .filter(t => !t.is_new)
          .map(t => t.id),
        // New tenants to create
        new_people: this.form.tenants
          .filter(t => t.is_new)
          .map(t => ({
            first_name: t.first_name,
            last_name: t.last_name,
            email: t.email,
            phone: t.phone
          }))
      }
    },

    // Create the tenancy
    async createTenancy() {
      if (!this.validateForm()) {
        return
      }

      this.saving = true
      this.formErrors = {}

      try {
        const response = await fetch('/tenancies.json', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify(this.buildTenancyPayload())
        })

        const data = await response.json()

        if (response.ok && data.success) {
          this.handleCreateSuccess(data.tenancy)
        } else {
          this.handleCreateError(data.errors || { base: ['Failed to create tenancy'] })
        }
      } catch (error) {
        console.error('Failed to create tenancy:', error)
        this.formErrors.base = 'An unexpected error occurred. Please try again.'
      } finally {
        this.saving = false
      }
    },

    // Handle successful tenancy creation
    handleCreateSuccess(tenancy) {
      // Switch to view mode showing the new tenancy
      this.mode = 'view'
      this.tenancyId = tenancy.id
      this.tenancy = tenancy
      this.openSections = { documents: true, history: true }
      this.noteForm = { content: '', saving: false }

      // Dispatch event for list updates
      window.dispatchEvent(new CustomEvent('tenancy-created', {
        detail: { tenancy }
      }))
    },

    // Handle creation errors
    handleCreateError(errors) {
      // Map server errors to form fields
      if (typeof errors === 'object') {
        Object.entries(errors).forEach(([field, messages]) => {
          this.formErrors[field] = Array.isArray(messages) ? messages[0] : messages
        })
      } else {
        this.formErrors.base = 'Failed to create tenancy'
      }
    }
  }
}
